import React, { useCallback, useRef, useEffect, useState, useMemo } from "react";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Input } from "../../ui/input";
import { RichTextEditor } from "../RichTextEditor";
import {
  AutomatedControlData,
  GitcControl,
  ConfigurationSetting,
  Variant,
  SourceData,
} from "../types-automated";
import { FREQUENCY_OPTIONS } from "../types";
import { Plus, Trash2 } from "lucide-react";
import { ControlOwnerSelectorDialog } from "./ControlOwnerSelectorDialog";
import { DependentControlSelectorDialog } from "./DependentControlSelectorDialog";
import { ControlDocumentManager } from "../ControlDocumentManager";

interface EvaluateDesignAutomatedProps {
  data: AutomatedControlData;
  onChange: (data: Partial<AutomatedControlData>) => void;
  controlId?: string;
}

interface RommSummary {
  rommId: string;
  description: string;
  assertion: string[];
  assessment: string;
}

export const EvaluateDesignAutomated: React.FC<EvaluateDesignAutomatedProps> = ({ data, onChange, controlId }) => {
  // Use ref to access latest onChange without causing callback recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const [showControlOwnerDialog, setShowControlOwnerDialog] = useState(false);
  const [showDependentControlDialog, setShowDependentControlDialog] = useState(false);
  const [rommSummaries, setRommSummaries] = useState<RommSummary[]>([]);
  const [loadingRomms, setLoadingRomms] = useState(false);

  // Memoize existing controls to prevent unnecessary re-renders
  const memoizedControlOwners = useMemo(() => data.controlOwners, [data.controlOwners]);
  const memoizedDependentControls = useMemo(() => data.dependentControls, [data.dependentControls]);

  // Configuration Setting helpers
  const addConfigurationSetting = useCallback(() => {
    const newSetting: ConfigurationSetting = {
      id: `config-${Date.now()}`,
      name: "",
      description: "",
    };
    onChangeRef.current({ configurationSettings: [...data.configurationSettings, newSetting] });
  }, [data.configurationSettings]);

  const removeConfigurationSetting = useCallback((id: string) => {
    onChangeRef.current({ configurationSettings: data.configurationSettings.filter((s) => s.id !== id) });
  }, [data.configurationSettings]);

  const updateConfigurationSetting = useCallback((id: string, field: keyof ConfigurationSetting, value: string) => {
    onChangeRef.current({
      configurationSettings: data.configurationSettings.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      ),
    });
  }, [data.configurationSettings]);

  // Variant helpers
  const addVariant = useCallback(() => {
    const newVariant: Variant = {
      id: `variant-${Date.now()}`,
      name: "",
      description: "",
    };
    onChangeRef.current({ variants: [...data.variants, newVariant] });
  }, [data.variants]);

  const removeVariant = useCallback((id: string) => {
    onChangeRef.current({ variants: data.variants.filter((v) => v.id !== id) });
  }, [data.variants]);

  const updateVariant = useCallback((id: string, field: keyof Variant, value: string) => {
    onChangeRef.current({
      variants: data.variants.map((v) => (v.id === id ? { ...v, [field]: value } : v)),
    });
  }, [data.variants]);

  // Source Data helpers - using ref pattern to avoid array dependencies
  const sourceDataRef = useRef(data.sourceData);
  useEffect(() => {
    sourceDataRef.current = data.sourceData;
  }, [data.sourceData]);

  const addSourceData = useCallback(() => {
    const sourceDataIndex = sourceDataRef.current.length + 1;
    const newSourceData: SourceData = {
      id: `SD-${sourceDataIndex}`, // Use SD-X as the actual ID
      name: `SD-${sourceDataIndex}`,
      description: "",
      hasGitcs: false,
      gitcDetails: "",
      gitcControls: [],
      gitcDocuments: [],
      gitcTextResponse: undefined,
      hasBusinessControls: false,
      businessControlDetails: "",
      directControls: [],
      directDocuments: [],
      directTextResponse: undefined,
    };
    onChangeRef.current({ sourceData: [...sourceDataRef.current, newSourceData] });
  }, []);

  const removeSourceData = useCallback((id: string) => {
    onChangeRef.current({ sourceData: sourceDataRef.current.filter((s) => s.id !== id) });
  }, []);

  const updateSourceData = useCallback((id: string, field: keyof SourceData, value: any) => {
    onChangeRef.current({
      sourceData: sourceDataRef.current.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    });
  }, []);

  // Create a stable map of callbacks using useRef - this prevents recreating callbacks on every render
  const gitcCallbacksRef = useRef<Map<string, {
    onDocumentsChange: (documents: any) => void;
    onTextResponseChange: (textResponse: any) => void;
  }>>(new Map());

  const directCallbacksRef = useRef<Map<string, {
    onDocumentsChange: (documents: any) => void;
    onTextResponseChange: (textResponse: any) => void;
  }>>(new Map());

  // Update the refs with callbacks for each source data item, only creating new ones if they don't exist
  data.sourceData.forEach(source => {
    if (!gitcCallbacksRef.current.has(source.id)) {
      gitcCallbacksRef.current.set(source.id, {
        onDocumentsChange: (documents: any) => {
          const updated = sourceDataRef.current.map((s) =>
            s.id === source.id ? { ...s, gitcDocuments: documents } : s
          );
          onChangeRef.current({ sourceData: updated });
        },
        onTextResponseChange: (textResponse: any) => {
          const updated = sourceDataRef.current.map((s) =>
            s.id === source.id ? { ...s, gitcTextResponse: textResponse } : s
          );
          onChangeRef.current({ sourceData: updated });
        }
      });
    }
    
    if (!directCallbacksRef.current.has(source.id)) {
      directCallbacksRef.current.set(source.id, {
        onDocumentsChange: (documents: any) => {
          const updated = sourceDataRef.current.map((s) =>
            s.id === source.id ? { ...s, directDocuments: documents } : s
          );
          onChangeRef.current({ sourceData: updated });
        },
        onTextResponseChange: (textResponse: any) => {
          const updated = sourceDataRef.current.map((s) =>
            s.id === source.id ? { ...s, directTextResponse: textResponse } : s
          );
          onChangeRef.current({ sourceData: updated });
        }
      });
    }
  });

  // Memoized callbacks for Select components
  const handleReliesOnSystemConfigChange = useCallback((value: string) => {
    onChangeRef.current({ reliesOnSystemConfiguration: value as "Yes" | "No" | "" });
  }, []);

  const handleHasVariationsChange = useCallback((value: string) => {
    onChangeRef.current({ hasVariations: value as "Yes" | "No" | "" });
  }, []);

  const handleDesignEffectiveChange = useCallback((value: string) => {
    onChangeRef.current({ isDesignEffective: value as "Yes" | "No" | "Not Yet Concluded" });
  }, []);

  const handleProperlyImplementedChange = useCallback((value: string) => {
    onChangeRef.current({ isProperlyImplemented: value as "Yes" | "No" | "Not Yet Concluded" });
  }, []);

  const handleFrequencyChange = useCallback((value: string) => {
    onChangeRef.current({ frequency: value });
  }, []);

  const handleControlOwnersSelected = useCallback((owners: typeof data.controlOwners) => {
    onChangeRef.current({ controlOwners: owners });
    setShowControlOwnerDialog(false);
  }, []);

  const handleCloseControlOwnerDialog = useCallback(() => {
    setShowControlOwnerDialog(false);
  }, []);

  const handleRemoveControlOwner = useCallback((ownerId: string) => {
    onChangeRef.current({
      controlOwners: data.controlOwners.filter((owner) => owner.id !== ownerId),
    });
  }, [data.controlOwners]);

  const handleDependentControlsSelected = useCallback((selectedControls: typeof data.dependentControls) => {
    onChangeRef.current({ dependentControls: selectedControls });
    setShowDependentControlDialog(false);
  }, []);

  const handleCloseDependentControlDialog = useCallback(() => {
    setShowDependentControlDialog(false);
  }, []);

  const handleRemoveDependentControl = useCallback((controlId: string) => {
    onChangeRef.current({
      dependentControls: data.dependentControls.filter((c) => c.id !== controlId),
    });
  }, [data.dependentControls]);

  // Load ROMM summaries for Design Factor 1
  useEffect(() => {
    const loadRommSummaries = async () => {
      if (!controlId) {
        setRommSummaries([]);
        return;
      }

      setLoadingRomms(true);
      try {
        // Step 1: Load Libraries_RCM.json to get romm array for this control
        let rommIds: string[] = [];
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadRcmJson === "function") {
          const rcmResult = await window.sharePointAPI.downloadRcmJson();
          if (rcmResult.success && rcmResult.data) {
            const librariesRcm = rcmResult.data.libraries_RCM || rcmResult.data.libraries_rcm || {};
            const controlData = librariesRcm[controlId];
            if (controlData && controlData.romm && Array.isArray(controlData.romm)) {
              rommIds = controlData.romm;
            }
          }
        }

        if (rommIds.length === 0) {
          setRommSummaries([]);
          setLoadingRomms(false);
          return;
        }

        // Step 2: Load Libraries_Romm.json to get ROMM details
        const summaries: RommSummary[] = [];
        if (window.sharePointAPI && typeof window.sharePointAPI.readRommLibrary === "function") {
          const rommResult = await window.sharePointAPI.readRommLibrary();
          if (rommResult.success && rommResult.data) {
            const rommLibrary = rommResult.data.romm_library || rommResult.data.rommLibrary || [];
            
            // Find each ROMM by ID
            for (const rommId of rommIds) {
              const romm = rommLibrary.find((r: any) => r["romm-id"] === rommId || r.rommId === rommId || r.romm_id === rommId);
              if (romm) {
                summaries.push({
                  rommId: romm["romm-id"] || romm.rommId || romm.romm_id || rommId,
                  description: romm.description || "",
                  assertion: Array.isArray(romm.assertion) ? romm.assertion : (romm.assertion ? [romm.assertion] : []),
                  assessment: romm.assesment || romm.assessment || "",
                });
              }
            }
          }
        }

        setRommSummaries(summaries);
      } catch (error) {
        console.error("Failed to load ROMM summaries:", error);
        setRommSummaries([]);
      } finally {
        setLoadingRomms(false);
      }
    };

    loadRommSummaries();
  }, [controlId]);

  // Helper function to get assessment icon
  const getAssessmentIcon = (assessment: string, rommId: string) => {
    const assessmentLower = assessment?.toLowerCase() || "";
    const uniqueId = rommId.replace(/[^a-zA-Z0-9]/g, '');
    
    if (assessmentLower === "lower") {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
          <defs>
            <mask id={`lowerMask${uniqueId}`}>
              <rect width="16" height="16" fill="black"/>
              <polygon points="8,2 14,14 2,14" fill="white"/>
            </mask>
          </defs>
          <polygon points="8,2 14,14 2,14" fill="none" stroke="#CCCC00" strokeWidth="1"/>
          <rect x="0" y="10" width="16" height="6" fill="#CCCC00" mask={`url(#lowerMask${uniqueId})`}/>
        </svg>
      );
    } else if (assessmentLower === "higher") {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
          <defs>
            <mask id={`higherMask${uniqueId}`}>
              <rect width="16" height="16" fill="black"/>
              <polygon points="8,2 14,14 2,14" fill="white"/>
            </mask>
          </defs>
          <polygon points="8,2 14,14 2,14" fill="none" stroke="#FFA500" strokeWidth="1"/>
          <rect x="0" y="8" width="16" height="8" fill="#FFA500" mask={`url(#higherMask${uniqueId})`}/>
        </svg>
      );
    } else if (assessmentLower === "significant") {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
          <polygon points="8,2 14,14 2,14" fill="#FF0000"/>
        </svg>
      );
    } else if (assessmentLower === "nrpmm") {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
          <polygon points="8,2 14,14 2,14" fill="none" stroke="#008000" strokeWidth="2"/>
        </svg>
      );
    }
    return null;
  };

  // Helper function to get assessment badge color
  const getAssessmentColor = (assessment: string) => {
    const assessmentLower = assessment?.toLowerCase() || "";
    switch (assessmentLower) {
      case 'higher':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'significant':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      case 'lower':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'nrpmm':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">
          Evaluate Design and Determine Implementation
        </h2>
      </div>
      <div className="p-6 space-y-8">
        {/* Control Description Display */}
        {data.controlDescription && (
          <div className="p-4 border border-white/10 rounded-lg bg-black/20">
            <Label className="text-sm font-medium text-white mb-2 block">
              Control Description
            </Label>
            <div
              className="text-white/60"
              dangerouslySetInnerHTML={{ __html: data.controlDescription }}
            />
          </div>
        )}

        {/* Associated IT Element */}
        <div className="space-y-4 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">Associated IT Element</h3>
          <p className="text-sm text-white/60">
            User can associate IT elements here (to be explained in detail in future)
          </p>
          <Button variant="outline" className="border-white/10 text-white hover:bg-white/10">
            + Associate IT Element
          </Button>
        </div>

        {/* Design & Implementation */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">i. Design & Implementation</h3>

          {/* GITC(s) over access and changes to automated control */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <h4 className="text-sm font-semibold text-white">
              Identify GITC(s) over access and changes to the automated control programmed functionality.
            </h4>
            <RichTextEditor
              label="GITC Details"
              value={data.proceduresToUnderstandDesign}
              onChange={(content) => onChangeRef.current({ proceduresToUnderstandDesign: content })}
              placeholder="Enter GITC details for access and changes..."
            />
          </div>

          {/* System Configuration */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <h4 className="text-sm font-semibold text-white">
              Does the automated control rely on a system configuration that is tailored through user defined settings?
            </h4>
            <select
              value={data.reliesOnSystemConfiguration || ""}
              onChange={(e) => handleReliesOnSystemConfigChange(e.target.value)}
              className="bg-black/20 border border-white/10 text-white w-48 px-3 py-2 rounded-md"
            >
              <option value="">Select Yes/No</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>

            {data.reliesOnSystemConfiguration === "Yes" && (
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-white">Configuration Settings</Label>
                  <Button
                    variant="outline"
                    onClick={addConfigurationSetting}
                    className="border-white/10 text-white hover:bg-white/10"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Configuration Setting
                  </Button>

                  {data.configurationSettings.length > 0 && (
                    <div className="border border-white/10 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-black/40">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Configuration Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Configuration Description</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-white/60">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.configurationSettings.map((setting) => (
                            <tr key={setting.id} className="border-t border-white/10">
                              <td className="px-4 py-3">
                                <Input
                                  value={setting.name}
                                  onChange={(e) => updateConfigurationSetting(setting.id, "name", e.target.value)}
                                  className="bg-black/20 border-white/10 text-white"
                                  placeholder="Enter name"
                                />
                              </td>
                              <td className="px-4 py-3">
                                <Input
                                  value={setting.description}
                                  onChange={(e) => updateConfigurationSetting(setting.id, "description", e.target.value)}
                                  className="bg-black/20 border-white/10 text-white"
                                  placeholder="Enter description"
                                />
                              </td>
                              <td className="px-4 py-3 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeConfigurationSetting(setting.id)}
                                  className="text-red-400 hover:text-red-300"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <RichTextEditor
                  label="What is the management's intended design of the configured values/settings?"
                  value={data.managementIntendedDesign}
                  onChange={(content) => onChangeRef.current({ managementIntendedDesign: content })}
                  placeholder="Enter management's intended design..."
                />

                <RichTextEditor
                  label="How is access to modify the configuration controlled?"
                  value={data.accessToModifyConfiguration}
                  onChange={(content) => onChangeRef.current({ accessToModifyConfiguration: content })}
                  placeholder="Enter access control details..."
                />

                <RichTextEditor
                  label="Does the configuration change frequently?"
                  value={data.configurationChangeFrequency}
                  onChange={(content) => onChangeRef.current({ configurationChangeFrequency: content })}
                  placeholder="Enter frequency of configuration changes..."
                />
              </div>
            )}

            {/* Procedures to understand design - shown for both Yes and No */}
            <div className="mt-4">
              <RichTextEditor
                label="Procedures to understand design of automated functionality and system configuration"
                value={data.proceduresToUnderstandDesign}
                onChange={(content) => onChangeRef.current({ proceduresToUnderstandDesign: content })}
                placeholder="Enter procedures..."
              />
            </div>
          </div>

          {/* GITC(s) over system configuration */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <h4 className="text-sm font-semibold text-white">
              Identify GITC(s) over access and changes to system configuration.
            </h4>
            <RichTextEditor
              label="GITC Details for System Configuration"
              value={data.accessToModifyConfiguration}
              onChange={(content) => onChangeRef.current({ accessToModifyConfiguration: content })}
              placeholder="Enter GITC details for system configuration..."
            />
          </div>

          {/* Variations */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <h4 className="text-sm font-semibold text-white">
              Are there any variations in how the automated control is applied?
            </h4>
            <select
              value={data.hasVariations || ""}
              onChange={(e) => handleHasVariationsChange(e.target.value)}
              className="bg-black/20 border border-white/10 text-white w-48 px-3 py-2 rounded-md"
            >
              <option value="">Select Yes/No</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>

            <RichTextEditor
              label="Procedures to understand and identify potential variants."
              value={data.proceduresToUnderstandVariants}
              onChange={(content) => onChangeRef.current({ proceduresToUnderstandVariants: content })}
              placeholder="Enter procedures..."
            />

            <RichTextEditor
              label="Additional procedures to understand design and determine implementation of the control."
              value={data.additionalProcedures}
              onChange={(content) => onChangeRef.current({ additionalProcedures: content })}
              placeholder="Enter additional procedures..."
            />

            {data.hasVariations === "Yes" && (
              <div className="space-y-2 mt-4">
                <Label className="text-white">Variants</Label>
                <Button
                  variant="outline"
                  onClick={addVariant}
                  className="border-white/10 text-white hover:bg-white/10"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Variant
                </Button>

                {data.variants.length > 0 && (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-black/40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Variant Name</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Variant Description</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-white/60">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.variants.map((variant) => (
                          <tr key={variant.id} className="border-t border-white/10">
                            <td className="px-4 py-3">
                              <Input
                                value={variant.name}
                                onChange={(e) => updateVariant(variant.id, "name", e.target.value)}
                                className="bg-black/20 border-white/10 text-white"
                                placeholder="Enter name"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <Input
                                value={variant.description}
                                onChange={(e) => updateVariant(variant.id, "description", e.target.value)}
                                className="bg-black/20 border-white/10 text-white"
                                placeholder="Enter description"
                              />
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeVariant(variant.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Source Data */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">ii. Source Data</h3>
          <p className="text-sm text-white/60">
            Understand the source data from which internal information is created and maintained in IT system.
          </p>

          <Button
            variant="outline"
            onClick={addSourceData}
            className="border-white/10 text-white hover:bg-white/10"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Source Data
          </Button>

          {data.sourceData.map((source) => (
            <div key={source.id} className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-white">Source Data: {source.name || "Unnamed"}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSourceData(source.id)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Source Data Name</Label>
                <Input
                  value={source.name}
                  onChange={(e) => updateSourceData(source.id, "name", e.target.value)}
                  className="bg-black/20 border-white/10 text-white"
                  placeholder="Enter source data name"
                />
              </div>

              <RichTextEditor
                label="Describe the information (or source data) used in the automated control."
                value={source.description}
                onChange={(content) => updateSourceData(source.id, "description", content)}
                placeholder="Enter description..."
              />

              <div className="space-y-2">
                <Label className="text-white">
                  Are there any GITCs over the system(s) that contains relevant source data relevant to the audit?
                </Label>
                <select
                  value={source.hasGitcs ? "Yes" : source.hasGitcs === false ? "No" : ""}
                  onChange={(e) => updateSourceData(source.id, "hasGitcs", e.target.value === "Yes")}
                  className="bg-black/20 border border-white/10 text-white w-48 px-3 py-2 rounded-md"
                >
                  <option value="">Select Yes/No</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {source.hasGitcs && (
                <div className="space-y-4">
                  <RichTextEditor
                    label="GITC Details"
                    value={source.gitcDetails}
                    onChange={(content) => updateSourceData(source.id, "gitcDetails", content)}
                    placeholder="Enter GITC details..."
                  />
                  <ControlDocumentManager
                    controlId={data.controlId}
                    workspaceLinked={data.workspaceLinked}
                    documents={source.gitcDocuments || []}
                    onDocumentsChange={gitcCallbacksRef.current.get(source.id)?.onDocumentsChange || (() => {})}
                    textResponse={source.gitcTextResponse}
                    onTextResponseChange={gitcCallbacksRef.current.get(source.id)?.onTextResponseChange || (() => {})}
                    label={`${source.name}-GITC`}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-white">Are there any business controls over the source data?</Label>
                <select
                  value={source.hasBusinessControls ? "Yes" : source.hasBusinessControls === false ? "No" : ""}
                  onChange={(e) => updateSourceData(source.id, "hasBusinessControls", e.target.value === "Yes")}
                  className="bg-black/20 border border-white/10 text-white w-48 px-3 py-2 rounded-md"
                >
                  <option value="">Select Yes/No</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>

              {source.hasBusinessControls && (
                <div className="space-y-4">
                  <RichTextEditor
                    label="Business Control Details"
                    value={source.businessControlDetails}
                    onChange={(content) => updateSourceData(source.id, "businessControlDetails", content)}
                    placeholder="Enter business control details..."
                  />
                  <ControlDocumentManager
                    controlId={data.controlId}
                    workspaceLinked={data.workspaceLinked}
                    documents={source.directDocuments || []}
                    onDocumentsChange={directCallbacksRef.current.get(source.id)?.onDocumentsChange || (() => {})}
                    textResponse={source.directTextResponse}
                    onTextResponseChange={directCallbacksRef.current.get(source.id)?.onTextResponseChange || (() => {})}
                    label={`${source.name}-Direct`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Design Factors */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">iii. Design Factors</h3>

          {/* Design Factor 1 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <h4 className="text-sm font-semibold text-white">
              Design Factor 1: Appropriateness of the purpose of control and its correlation to the risk/assertion identified and/or RAIT
            </h4>
            
            {loadingRomms ? (
              <div className="p-4 border border-white/10 rounded-lg bg-black/40 text-white/60 text-center">
                Loading associated RoMMs...
              </div>
            ) : rommSummaries.length === 0 ? (
              <div className="p-4 border border-white/10 rounded-lg bg-black/40 text-white/60 text-center">
                No RoMMs associated with this control.
              </div>
            ) : (
              <div className="space-y-4">
                {/* Display all ROMM summaries */}
                {rommSummaries.map((romm, index) => (
                  <div key={romm.rommId} className="p-4 border border-white/10 rounded-lg bg-black/40">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div>
                            <span className="text-xs font-medium text-white/60">RoMM ID:</span>
                            <span className="ml-2 text-sm font-semibold text-white">{romm.rommId}</span>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-white/60">Description:</span>
                            <p className="mt-1 text-sm text-white">{romm.description}</p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-white/60">Assertion:</span>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {romm.assertion.map((assert, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 rounded bg-white/10 border border-white/20 text-xs text-white"
                                >
                                  {assert}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        {/* Assessment in top right */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {getAssessmentIcon(romm.assessment, romm.rommId)}
                          <span className={`text-xs px-2 py-1 rounded border ${getAssessmentColor(romm.assessment)}`}>
                            {romm.assessment}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Single Rich Text Editor for all ROMMs */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <Label className="text-sm font-medium text-white mb-2 block">
                    Response:
                  </Label>
                  <RichTextEditor
                    value={data.designFactor1Response || ""}
                    onChange={(content) => {
                      onChangeRef.current({ designFactor1Response: content });
                    }}
                    placeholder="Enter response for Design Factor 1..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Design Factor 2 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">
                Design Factor 2: Competence and authority of the person(s) performing the control
              </h4>
              <Switch
                checked={data.designFactor2Enabled}
                onCheckedChange={(checked) => onChangeRef.current({ designFactor2Enabled: checked })}
              />
            </div>

            {data.designFactor2Enabled && (
              <div className="space-y-4">
                <Button 
                  variant="outline" 
                  className="border-white/10 text-white hover:bg-white/10"
                  onClick={() => setShowControlOwnerDialog(true)}
                >
                  + Associate Control Owner
                </Button>

                {data.controlOwners.length > 0 ? (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-black/40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Control Owner</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Job Title</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">Background Information</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-white/60">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.controlOwners.map((owner) => (
                          <tr key={owner.id} className="border-t border-white/10">
                            <td className="px-4 py-3 text-white">{owner.name}</td>
                            <td className="px-4 py-3 text-white">{owner.jobTitle}</td>
                            <td className="px-4 py-3 text-white">{owner.background}</td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveControlOwner(owner.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 border border-white/10 rounded-lg bg-black/20 text-white/60 text-sm text-center">
                    No control owners associated. Click "+ Associate Control Owner" to add.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Design Factor 3 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">
                Design Factor 3: Frequency and consistency with which the control is performed
              </h4>
              <Switch
                checked={data.designFactor3Enabled}
                onCheckedChange={(checked) => onChangeRef.current({ designFactor3Enabled: checked })}
              />
            </div>

            {data.designFactor3Enabled && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white">Frequency</Label>
                  <select
                    value={data.frequency || ""}
                    onChange={(e) => handleFrequencyChange(e.target.value)}
                    className="bg-black/20 border border-white/10 text-white px-3 py-2 rounded-md"
                  >
                    <option value="">Select frequency</option>
                    {FREQUENCY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <RichTextEditor
                  label="Consistency"
                  value={data.consistency}
                  onChange={(content) => onChangeRef.current({ consistency: content })}
                  placeholder="Enter consistency details..."
                />
              </div>
            )}
          </div>

          {/* Design Factor 4 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">
                Design Factor 4: Level of aggregation and predictability
              </h4>
              <Switch
                checked={data.designFactor4Enabled}
                onCheckedChange={(checked) => onChangeRef.current({ designFactor4Enabled: checked })}
              />
            </div>

            {data.designFactor4Enabled && (
              <RichTextEditor
                value={data.designFactor4Content}
                onChange={(content) => onChangeRef.current({ designFactor4Content: content })}
                placeholder="Enter details..."
              />
            )}
          </div>

          {/* Design Factor 5 */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-white">
                Design Factor 5: Criteria for investigation (threshold) and process for follow-up
              </h4>
              <Switch
                checked={data.designFactor5Enabled}
                onCheckedChange={(checked) => onChangeRef.current({ designFactor5Enabled: checked })}
              />
            </div>

            {data.designFactor5Enabled && (
              <RichTextEditor
                value={data.designFactor5Content}
                onChange={(content) => onChangeRef.current({ designFactor5Content: content })}
                placeholder="Enter details..."
              />
            )}
          </div>
        </div>

        {/* Dependent Controls */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">iv. Dependent Controls</h3>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Label className="text-white">Is the control dependent on other controls?</Label>
              <select
                value={data.isDependentOnOtherControls ? "Yes" : "No"}
                onChange={(e) =>
                  onChangeRef.current({ isDependentOnOtherControls: e.target.value === "Yes" })
                }
                className="w-32 bg-black/20 border border-white/10 text-white px-3 py-2 rounded-md"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            {data.isDependentOnOtherControls && (
              <div className="space-y-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDependentControlDialog(true)} 
                  className="border-white/10 text-white hover:bg-white/10"
                >
                  + Add
                </Button>

                {data.dependentControls.length > 0 ? (
                  <div className="border border-white/10 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-black/40">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Control ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Control Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Type of Control
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                            Nature
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-white/60">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.dependentControls.map((control) => (
                          <tr key={control.id} className="border-t border-white/10">
                            <td className="px-4 py-3 text-white">
                              {control.controlId || "-"}
                            </td>
                            <td className="px-4 py-3 text-white">
                              {control.controlName || "-"}
                            </td>
                            <td className="px-4 py-3 text-white">
                              {control.controlDescription || "-"}
                            </td>
                            <td className="px-4 py-3 text-white">
                              {control.designImplementation || "-"}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveDependentControl(control.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-4 border border-white/10 rounded-lg bg-black/20 text-white/60 text-sm text-center">
                    No dependent controls associated. Click "+ Add" to add.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Effectiveness of Design & Implementation */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">v. Effectiveness of Design & Implementation</h3>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white">Is the design of this control effective?</Label>
              <select
                value={data.isDesignEffective || "Not Yet Concluded"}
                onChange={(e) => handleDesignEffectiveChange(e.target.value)}
                className="bg-black/20 border border-white/10 text-white px-3 py-2 rounded-md"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Not Yet Concluded">Not Yet Concluded</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-white">Is this control properly implemented?</Label>
              <select
                value={data.isProperlyImplemented || "Not Yet Concluded"}
                onChange={(e) => handleProperlyImplementedChange(e.target.value)}
                className="bg-black/20 border border-white/10 text-white px-3 py-2 rounded-md"
              >
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Not Yet Concluded">Not Yet Concluded</option>
              </select>
            </div>
          </div>
        </div>

        {/* Control Owner Selector Dialog */}
        <ControlOwnerSelectorDialog
          isOpen={showControlOwnerDialog}
          onClose={handleCloseControlOwnerDialog}
          onSelect={handleControlOwnersSelected}
          existingOwners={memoizedControlOwners}
        />

        {/* Dependent Control Selector Dialog */}
        <DependentControlSelectorDialog
          isOpen={showDependentControlDialog}
          onClose={handleCloseDependentControlDialog}
          onSelect={handleDependentControlsSelected}
          existingControls={memoizedDependentControls}
        />
      </div>
    </div>
  );
};

